import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Plus, Sparkles, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { canvasAIApi } from '@/lib/api/client'
import type { CanvasSection } from './canvasTemplates'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { alpha, getCanvasSectionThemeStyles } from './canvasTheme'

// Suggestion from AI
interface AISuggestion {
  id: string
  label: string
  description?: string
  entityType: string
  confidence: 'high' | 'medium' | 'low'
  reasoning?: string
}

interface SectionSuggestionsPopupProps {
  section: CanvasSection
  canvasInstanceUri: string
  accountId: string
  projectId: string
  apiKey: string
  canvasContext?: Record<string, string[]>
  isOpen: boolean
  onClose: () => void
  onAddSuggestion: (suggestion: {
    type: string
    label: string
    description?: string
    sectionId: string
    confidence: number
  }) => void
  anchorRef?: React.RefObject<HTMLElement | null>
}

export function SectionSuggestionsPopup({
  section,
  canvasInstanceUri,
  accountId,
  projectId,
  apiKey,
  canvasContext,
  isOpen,
  onClose,
  onAddSuggestion,
  anchorRef,
}: SectionSuggestionsPopupProps) {
  const themeStyles = getCanvasSectionThemeStyles(section.theme)
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())
  const popupRef = useRef<HTMLDivElement>(null)

  // Fetch suggestions when popup opens
  const fetchSuggestions = useCallback(async () => {
    if (!isOpen || !apiKey) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await canvasAIApi.generateSuggestions(
        accountId,
        projectId,
        canvasInstanceUri,
        {
          sectionKey: section.id,
          apiKey,
          canvasContext,
          limit: 5,
        }
      )
      setSuggestions(result.suggestions)
    } catch (err) {
      console.error('Failed to fetch suggestions:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions')
    } finally {
      setIsLoading(false)
    }
  }, [isOpen, apiKey, accountId, projectId, canvasInstanceUri, section.id, canvasContext])

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions()
      setAddedIds(new Set())
    }
  }, [isOpen, fetchSuggestions])

  // Handle clicking outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        anchorRef?.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, anchorRef])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Handle adding a suggestion
  const handleAddSuggestion = (suggestion: AISuggestion) => {
    const confidenceMap: Record<string, number> = {
      high: 0.9,
      medium: 0.7,
      low: 0.5,
    }

    onAddSuggestion({
      type: suggestion.entityType,
      label: suggestion.label,
      description: suggestion.description,
      sectionId: section.id,
      confidence: confidenceMap[suggestion.confidence] || 0.7,
    })

    setAddedIds((prev) => new Set([...prev, suggestion.id]))
  }

  if (!isOpen) return null

  // Confidence badge colors
  const confidenceColors: Record<string, { className: string }> = {
    high: { className: 'bg-success-bg text-success-fg border border-success/20' },
    medium: { className: 'bg-warning-bg text-warning-fg border border-warning/20' },
    low: { className: 'bg-danger-bg text-danger-fg border border-danger/20' },
  }

  return (
    <div
      ref={popupRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '4px',
        width: '320px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--color-border)',
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderBottom: '1px solid var(--color-border)',
          backgroundColor: alpha(section.color, 0.08),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles style={{ width: '16px', height: '16px', color: section.color }} />
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)' }}>
            AI Suggestions
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {!isLoading && (
            <Button
              onClick={fetchSuggestions}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              title="Refresh suggestions"
            >
              <RefreshCw className="h-3.5 w-3.5 text-text-muted" />
            </Button>
          )}
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
          >
            <X className="h-3.5 w-3.5 text-text-muted" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        {isLoading ? (
          <div
            style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Loader2
              style={{
                width: '24px',
                height: '24px',
                color: section.color,
                animation: 'spin 1s linear infinite',
              }}
            />
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              Generating suggestions...
            </span>
          </div>
        ) : error ? (
          <div
            style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <AlertCircle style={{ width: '24px', height: '24px', color: 'var(--color-danger)' }} />
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{error}</span>
            <Button
              onClick={fetchSuggestions}
              variant={themeStyles.badgeVariant}
              size="sm"
            >
              Try again
            </Button>
          </div>
        ) : suggestions.length === 0 ? (
          <div
            style={{
              padding: '32px',
              textAlign: 'center',
              color: 'var(--color-text-muted)',
              fontSize: '12px',
            }}
          >
            No suggestions available. Try adding more content to other sections first.
          </div>
        ) : (
          <div style={{ padding: '8px' }}>
            {suggestions.map((suggestion) => {
              const isAdded = addedIds.has(suggestion.id)
              const colors = confidenceColors[suggestion.confidence] || confidenceColors.medium

              return (
                <div
                  key={suggestion.id}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: isAdded ? 'var(--color-bg-secondary)' : 'transparent',
                  marginBottom: '4px',
                  transition: 'background-color 0.15s',
                }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '6px',
                          marginBottom: '4px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: isAdded ? 'var(--color-text-muted)' : 'var(--color-text)',
                            textDecoration: isAdded ? 'line-through' : 'none',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {suggestion.label}
                        </span>
                        <Badge className={colors.className} style={{ fontSize: '10px', textTransform: 'capitalize' }}>
                          {suggestion.confidence}
                        </Badge>
                      </div>
                      {suggestion.description && (
                        <p
                          style={{
                            fontSize: '11px',
                            color: 'var(--color-text-muted)',
                            lineHeight: 1.4,
                            margin: 0,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {suggestion.description}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => handleAddSuggestion(suggestion)}
                      disabled={isAdded}
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 flex-shrink-0"
                      style={{
                        borderColor: isAdded ? 'var(--color-border)' : alpha(section.color, 0.25),
                        opacity: isAdded ? 0.5 : 1,
                      }}
                      title={isAdded ? 'Added' : 'Add to canvas'}
                    >
                      <Plus
                        style={{
                          width: '14px',
                          height: '14px',
                          color: isAdded ? 'var(--color-text-muted)' : section.color,
                        }}
                      />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer - Section context */}
      <div
        style={{
          padding: '10px 14px',
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-bg-secondary)',
        }}
      >
        <p style={{ fontSize: '10px', color: 'var(--color-text-muted)', margin: 0 }}>
          Suggestions for: {section.title}
        </p>
      </div>
    </div>
  )
}
